import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { httpRetryer } from '@frontend/util/http-utilities';
import { ErrorLogoSVG } from '@shared-assets/images';
import {
  PageHeaderButtonsComponent,
  PageHeaderComponent,
} from '@terminus-ui/page-header';
import { TokenStorageService } from '@util/token-storage';
import { AuthService, MyTerminusIdpProvider } from 'auth';
import { EnvService } from 'frontend-environment';
import { applicationManagerMessages } from 'grpc-gateway-generated';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import {
  ApplicationManagerApiService,
  Config,
  ConfigKind,
  ConfigType,
  ConfigValue,
} from '../../application-manager-api.service';
import { CustomerService } from '../../customer.service';

@Component({
  selector: 'lib-adex-configuration',
  templateUrl: './adex-configuration.component.html',
  imports: [
    ButtonModule,
    CommonModule,
    DialogModule,
    SelectModule,
    ChipModule,
    ToggleButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    TableModule,
    PageHeaderComponent,
    PageHeaderButtonsComponent,
    PanelModule,
    DatePickerModule,
    InputNumberModule,
    ToggleSwitchModule,
    ToastModule,
  ],
  providers: [DialogService],
})
export class AdexConfigurationComponent implements OnInit {
  errorLogo = ErrorLogoSVG;

  private applicationManagerApi = inject(ApplicationManagerApiService);
  private myTerminusService = inject(MyTerminusIdpProvider);
  private customerService = inject(CustomerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private httpClient = inject(HttpClient);
  private env = inject<{ ADEX_BACKEND_URL: string; MY_TERMINUS_URL: string }>(
    EnvService,
  );
  private authService = inject(AuthService);
  private tokenStorage = inject(TokenStorageService);

  /**
   * configs need to get updated individually
   * ```
    OneOf<{
      demand_science_cpm: boolean;
    }>;
    ```
   */
  marketerConfigs = new FormGroup({
    demand_science_cpm: new FormControl(false, { nonNullable: true }),
  });

  configKinds: ConfigKind[];
  configValues: ConfigValue[];
  configs: Config[] = [];
  contextualListId: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  contextualListName: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  contextualChips: { id: string; name: string }[] = [];
  contextualListChanged = false;

  public getConfigKind(uuid: string): ConfigKind | undefined {
    return this.configKinds.find((configKind) => configKind.uuid === uuid);
  }

  private customerData$ = this.activatedRoute.paramMap.pipe(
    map((paramMap) => {
      if (paramMap.has('id')) return paramMap.get('id');
      return '';
    }),
    switchMap((customerId) =>
      this.customerService.getCustomerById(customerId || ''),
    ),
  );

  private adexInstanceData$: Observable<
    applicationManagerMessages.Instance | undefined
  > = this.customerData$.pipe(
    switchMap(() => this.activatedRoute.paramMap),
    switchMap((paramMap) =>
      this.customerService
        .getCustomerAppInstances(paramMap.get('id') || '')
        .pipe(
          map((instances) =>
            instances?.find(
              (instance) => instance.instanceId === paramMap.get('instanceId'),
            ),
          ),
        ),
    ),
    shareReplay(1),
  );

  private adexMarketerData$ = combineLatest([
    this.authService.myTerminusToken$,
    this.adexInstanceData$,
  ]).pipe(
    switchMap(([token, instance]) =>
      this.myTerminusService.getMarketerByUUID(
        token as string,
        instance?.instanceId || '',
      ),
    ),
  );

  data$ = combineLatest({
    customer: this.customerData$,
    instance: this.adexInstanceData$,
    marketer: this.adexMarketerData$,
  }).pipe(
    startWith({
      customer: null,
      instance: null,
      marketer: null,
    }),
  );

  ngOnInit() {
    /** setup formData for features */
    firstValueFrom(this.data$.pipe(skip(1))).then((data) => {
      this.marketerConfigs.patchValue({
        demand_science_cpm: data.marketer?.['demand_science_cpm'] || false,
      });
    });

    // get config kinds
    this.applicationManagerApi
      .getConfigKinds()
      .pipe(
        take(1),
        switchMap((configKinds) => {
          this.configKinds = configKinds.results;
          return this.data$.pipe(
            skip(1),
            filter((data) => !!data.marketer?.uuid),
            switchMap((data) =>
              this.applicationManagerApi.getConfig(data.marketer?.uuid || ''),
            ),
          );
        }),
      )
      .subscribe((configs) => {
        this.configValues = configs.results;
        this.createConfigs();
      });
  }

  createConfigs() {
    this.configKinds.forEach((configKind) => {
      const existingConfigValue = this.configValues.find(
        (configValue) => configValue.configKindUuid === configKind.uuid,
      );
      const config: Config = {
        configKind,
        configValue:
          existingConfigValue ||
          this.generateDefaultConfigValue(configKind.type, configKind.uuid),
        fc: new FormControl<any>(undefined),
      };

      if (configKind.type === ConfigType.STRING) {
        config.fc = new FormControl<string>(
          existingConfigValue?.value.valueString || '',
        );
      } else if (configKind.type === ConfigType.INT) {
        config.fc = new FormControl<number>(
          existingConfigValue?.value.valueInt || 0,
        );
      } else if (configKind.type === ConfigType.BOOLEAN) {
        config.fc = new FormControl<boolean>(
          existingConfigValue?.value.valueBool || false,
        );
      } else if (configKind.type === ConfigType.MAP) {
        if (existingConfigValue) {
          Object.entries(existingConfigValue.value.valueMap).forEach(
            ([key, value]) =>
              this.contextualChips.push({
                id: key,
                name: (value as any).valueString,
              }),
          );
        }
      }
      this.configs.push(config);
    });
  }

  generateDefaultConfigValue(
    configType: ConfigType,
    configKindUuid: string,
  ): ConfigValue {
    return {
      cou: {
        claim: 'sub.mar',
        value: '',
      },
      configKindUuid,
      uuid: '',
      createdAt: '',
      value: {
        valueString: '',
        valueInt: 0,
        valueBool: false,
        valueMap: {},
        isString: configType === ConfigType.STRING,
        isInt: configType === ConfigType.INT,
        isBool: configType === ConfigType.BOOLEAN,
        isMap: configType === ConfigType.MAP,
      },
    };
  }

  setConfig(config: Config) {
    if (config.configKind.type === ConfigType.INT) {
      config.configValue.value.valueInt = config.fc.value;
    } else if (config.configKind.type === ConfigType.BOOLEAN) {
      config.configValue.value.valueBool = config.fc.value;
    } else if (config.configKind.type === ConfigType.STRING) {
      config.configValue.value.valueString = config.fc.value;
    } else if (config.configKind.type === ConfigType.MAP) {
      this.contextualListChanged = false;
      // reset state of valueMap and make it equal to the chips that are currently present
      config.configValue.value.valueMap = {};
      if (!this.contextualChips.length) {
        // for whatever reason, maps need to be explicitly unset if they are empty
        this.applicationManagerApi
          .unsetConfig(config.configValue.uuid)
          .pipe(take(1))
          .subscribe();
        return;
      } else {
        this.contextualChips.forEach((chip) => {
          config.configValue.value.valueMap[chip.id] = {
            valueString: chip.name,
            valueInt: 0,
            valueBool: false,
            valueMap: {},
            isString: true,
            isInt: false,
            isBool: false,
            isMap: false,
          };
        });
      }
    }
    this.data$
      .pipe(
        skip(1),
        filter((data) => !!data.marketer?.uuid),
        switchMap((data) => {
          config.configValue.cou.value = data.marketer?.uuid || '';
          return this.applicationManagerApi.setConfig(config.configValue);
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            detail: 'Successfully saved changes.',
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Unable to save changes.',
            life: 3000,
          });
        },
      });
  }

  addContextualList() {
    // don't add chips with same id
    if (
      this.contextualChips.find((c) => c.id === this.contextualListId.value)
    ) {
      return;
    } else {
      this.contextualListChanged = true;
      this.contextualChips.push({
        id: this.contextualListId.value,
        name: this.contextualListName.value,
      });
    }
  }

  removeContextualList(chip: { id: string; name: string }, config: Config) {
    this.contextualListChanged = true;
    this.contextualChips = this.contextualChips.filter((c) => c.id !== chip.id);
  }

  removeAllContextualLists() {
    this.contextualListChanged = true;
    this.contextualChips = [];
  }

  /** Not implemented yet */
  decommissionInstance(instance: applicationManagerMessages.Instance) {
    firstValueFrom(this.applicationManagerApi.decommissionInstance(instance))
      .then(() => {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      })
      .catch();
  }

  /** Not implemented yet */
  changeInstanceStatus() {
    throw new Error('Method not implemented.');
  }

  updateDemandScienceCPM(form: {
    demand_science_cpm: boolean;
    marketerUuid: string;
  }) {
    const baseUrl = this.env.ADEX_BACKEND_URL;

    firstValueFrom(
      this.authService.actualUserHubIdpToken$.pipe(
        switchMap((authToken) =>
          this.httpClient
            .get(`${this.env.MY_TERMINUS_URL}/v3/authorize`, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
            .pipe(httpRetryer({})),
        ),
        switchMap((authResponse) =>
          this.httpClient
            .post(
              `${baseUrl}/api/v2/update_marketer`,
              {
                marketer: {
                  uuid: form.marketerUuid,
                  demand_science_cpm: form.demand_science_cpm,
                },
              },
              { headers: { Authorization: `Bearer ${authResponse['token']}` } },
            )
            .pipe(httpRetryer({})),
        ),
      ),
    )
      .then((response) => {
        this.messageService.add({
          severity: 'success',
          detail: 'Successfully saved changes.',
          life: 3000,
        });
      })
      .catch((err) => {
        this.messageService.add({
          severity: 'error',
          detail: 'Unable to save changes.',
          life: 3000,
        });
      });
  }
}
